import * as codeSystems from '@seen/core/codeSystems'
import type { CodingDetails } from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import { ASSESSMENT_QUESTIONS_SYSTEM } from '@seen/core/terminology/types'
import type { JSX } from 'react'
import { ObservationCodeCounts } from '#src/components/rich-text-editor/mention/ObservationCodeCounts.tsx'
import { observationDetailsSummary } from '#src/components/rich-text-editor/mention/observationDetailsSummary.ts'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

const SYSTEM_NAMES = new Map([
  [codeSystems.LOINC_CODE_SYSTEM, 'LOINC'],
  [ASSESSMENT_QUESTIONS_SYSTEM, 'Library'],
  ['http://snomed.info/sct', 'SNOMED'],
])

export interface ObservationOptionLabelProps {
  system: string
  code: string
  display: string
  // The question as it is asked, when it says more than the display.
  definition?: string
  // Known for our own codes without asking the server.
  details?: CodingDetails
  // A LOINC panel: placed as a set of readings, not one value.
  panel?: boolean
}

// A row in the observation picker: what the code is, where it comes from,
// and, for our own codes, how it is answered.
export function ObservationOptionLabel({
  system,
  code,
  display,
  definition,
  details,
  panel = false,
}: ObservationOptionLabelProps): JSX.Element {
  // How often each code is recorded, so a clinician can pick the one in use.
  const showCounts = useFeatureFlag(featureFlags.structuredCapture)
  const participant = useOptionalParticipantReference()
  return (
    <div
      className="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5"
      {...(panel ? { 'data-panel-option': '' } : {})}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="min-w-0 truncate">{display}</span>
          {panel ? (
            <span className="shrink-0 rounded-sm border px-1 text-[10px] tracking-wide text-muted-foreground uppercase">
              Panel
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {SYSTEM_NAMES.get(system) ?? system}
          {' · '}
          <span className="font-mono">{code}</span>
          {showCounts && !panel ? (
            <ObservationCodeCounts
              system={system}
              code={code}
              participant={participant}
            />
          ) : null}
        </span>
      </div>
      {definition && definition !== display ? (
        <span className="line-clamp-1 text-xs text-muted-foreground">
          {definition}
        </span>
      ) : null}
      {details ? (
        <span className="line-clamp-1 text-xs text-muted-foreground">
          {observationDetailsSummary(details)}
        </span>
      ) : null}
    </div>
  )
}
